/**
 * ApplicationCard.jsx — RC 1.53 Applications-page redesign
 *                       + RC 1.53 polish/preview stabilisation slice
 *                       (operator-locked, 2026-05-12 PM).
 *
 * Strict holds (still in force):
 *   • Frontend-only. No backend / orchestration / billing /
 *     finalize-charge / controller / polling / prompts / schema /
 *     Documents-page / Workspace / Analyse-Fit edits.
 *   • Only existing backend endpoints are used:
 *       — PATCH `/api/application-packs/:id`           (status + notes)
 *       — GET   `/api/application-packs/:id/export-bundle` (download)
 *       — GET   `/api/cv-variants/:id`                 (preview body)
 *       — GET   `/api/cover-letters/:id`               (preview body)
 *       — GET   `/api/base-cvs/:id`                    (preview body)
 *     Every one of these is already used by PackPreviewPanel /
 *     ApplicationPackDetailPage. No new endpoints introduced.
 *
 * Operator-approved improvements (2026-05-12 PM):
 *   1. Source-CV / Tailored-CV / Cover-Letter / Interview-Prep
 *      "readiness chips" now also carry the artefact-accurate score
 *      inline (e.g. "Source CV 94 · Tailored CV — · Cover Letter ·
 *      Interview Prep"). Missing artefacts are muted +
 *      strike-through, not clickable.
 *   2. Old single "Source CV fit XX" / "Tailored CV fit XX" header
 *      badge REMOVED — scores now live inside the chips.
 *   3. Governance pill (Shortlist / Review / Reject) sources from
 *      `pack.canonical_fit.final_decision` first then falls back to
 *      the older `pack.pack_snapshot.fit.final_decision`. If neither
 *      is populated (e.g. a freshly-created pack without an
 *      Analyse-Fit run) the pill gracefully hides — no fabricated
 *      verdict.
 *   4. Preview now lazy-fetches the full artefact body for Tailored
 *      CV / Cover Letter / Source CV via the existing read
 *      endpoints; Interview Prep continues to render from the
 *      already-loaded pack snapshot.
 *   5. Expanded layout = two-column grid:
 *        LEFT  : Status select · Notes textarea · Save / Cancel
 *        RIGHT : "Preview included artefacts" stack
 *      Pack-Detail deep link lives at the bottom of the left column.
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  Briefcase, MapPin, FileText, Mail, Download, Send,
  PanelsTopLeft, ChevronDown, ChevronUp, CheckCircle2, MinusCircle,
  Loader2, Save, X, AlertTriangle, Sparkles,
  ShieldAlert, ShieldCheck, ShieldQuestion, ChevronRight, Eye, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { invalidate as cacheInvalidate } from '@/lib/listCache';
import CvRenderer from '@/components/render/CvRenderer';
import SendViaGmailDialog from '@/components/SendViaGmailDialog';
import {
  useWorkspaceAcknowledgement,
  WorkspaceAcknowledgementDialog,
} from '@/components/workspace/WorkspaceAcknowledgement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Status pill table (operator-locked palette). ─────────────────
const STATUS_OPTIONS = [
  { value: 'draft',     label: 'Draft',
    className: 'bg-ndor-warning-light text-ndor-warning border-ndor-warning/30' },
  { value: 'ready',     label: 'Ready',
    className: 'bg-ndor-info-light text-ndor-info border-ndor-info/30' },
  { value: 'submitted', label: 'Submitted',
    className: 'bg-ndor-success-light text-ndor-success border-ndor-success/30' },
  { value: 'archived',  label: 'Archived',
    className: 'bg-ndor-surface-alt text-ndor-ink-muted border-ndor-edge' },
];
function statusMeta(s) { return STATUS_OPTIONS.find((x) => x.value === s) || STATUS_OPTIONS[0]; }

// ── Governance pill — gracefully hides when data is absent. ───────
function recommendationPill(finalDecision) {
  const d = String(finalDecision || '').toLowerCase();
  if (!d) return null;
  if (d === 'shortlist' || d === 'pass' || d === 'strong') {
    return { label: 'Shortlist', tone: 'positive', Icon: ShieldCheck,
      className: 'bg-ndor-success-light text-ndor-success border-ndor-success/30' };
  }
  if (d === 'reject' || d === 'fail' || d === 'poor') {
    return { label: 'Reject', tone: 'negative', Icon: ShieldAlert,
      className: 'bg-ndor-danger-light text-ndor-danger border-ndor-danger/30' };
  }
  if (d === 'hold' || d === 'borderline' || d === 'review' || d === 'maybe') {
    return { label: 'Review', tone: 'caution', Icon: AlertTriangle,
      className: 'bg-ndor-warning-light text-ndor-warning border-ndor-warning/30' };
  }
  return { label: d.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    tone: 'neutral', Icon: ShieldQuestion,
    className: 'bg-ndor-surface-alt text-ndor-ink-muted border-ndor-edge' };
}

// ── U-14 — CV-sub-score materiality constants ────────────────────
//
// OPERATOR-LOCKED (2026-02-15). The chip strip and the preview
// subtitle both compare TAILORED CV sub-score against SOURCE CV
// sub-score. The materiality threshold mirrors the backend's
// `cv_quality_improved` charge gate (`TOLERANCE_BAND_POINTS + 1`
// in `routes/cv_variants.py` L2624) — what the wallet was charged
// for ≡ what the chip celebrates. Anything below this delta is
// surfaced as "Not needed" so the user sees the same truth the
// billing engine saw.
export const U14_MATERIALITY_DELTA = 6;

// ── computeChipDisplay (U-14.A) ──────────────────────────────────
// Pure, side-effect-free helper that resolves the 4-state truth
// table for the Tailored CV chip + the corresponding Source CV
// chip semantics. Extracted (and EXPORTED) so the source-pin
// tests can drive it directly without rendering React.
//
// Truth table (operator-locked):
//   1. variant missing                         → { kind:'na' }
//   2. variant present, tailoredSub missing    → { kind:'na' }
//   3. variant present, both subs, Δ ≥ +6      → { kind:'score', text:String(tailoredSub) }
//   4. variant present, both subs, Δ <  +6     → { kind:'not_needed' }
//   • sourceSub null + variant present + tailored present → 'legacy'
//     (we cannot compute Δ so cannot declare material improvement;
//     render the raw tailored sub-score with a 'legacy' kind so
//     the chip flags the missing comparator without lying.)
export function computeChipDisplay({ variantExists, sourceSub, tailoredSub }) {
  if (!variantExists) return { kind: 'na', text: 'N/A', raw: null };
  if (typeof tailoredSub !== 'number') return { kind: 'na', text: 'N/A', raw: null };
  if (typeof sourceSub !== 'number') {
    // Variant + tailored sub-score known, source sub-score unknown
    // (legacy snapshot pre-U-14.B OR job_scores row purged). We
    // cannot compute Δ, so we cannot declare material improvement.
    // Render the raw tailored sub-score with a 'legacy' kind so
    // the chip can flag the missing comparator without lying.
    return { kind: 'legacy', text: String(tailoredSub), raw: tailoredSub };
  }
  const delta = tailoredSub - sourceSub;
  if (delta >= U14_MATERIALITY_DELTA) {
    return { kind: 'score', text: String(tailoredSub), raw: tailoredSub };
  }
  return { kind: 'not_needed', text: 'Not needed', raw: tailoredSub };
}

// ── Pack readiness model — derives the four artefact rows. ──────
//
// SEMANTICS (operator-locked, 2026-05-12 PM polish slice;
// U-14 chip-source-of-truth rewrite, 2026-02-15):
//
//   • SOURCE CV is PROVENANCE only — it is NEVER exported in the
//     application pack. It appears in the compact summary strip
//     for context (with its CV SUB-SCORE — NOT the overall fit
//     score — per the U-14 contract) but it is NOT shown in the
//     expanded preview area. The pack export includes only the
//     Tailored CV, Cover Letter, and Interview Prep + governance
//     artefacts.
//
//   • TAILORED CV score is the variant's CV SUB-SCORE. The chip
//     renders the number ONLY when there is material improvement
//     (Δ ≥ +6) over the Source CV's sub-score. Below threshold
//     → "Not needed". Variant missing OR tailored sub-score
//     missing → "N/A". Legacy snapshot without source sub-score
//     → tailored sub-score rendered as 'legacy' kind so the user
//     can see the number without an implied comparison.
//
//   • COVER LETTER / INTERVIEW PREP carry no score on the strip.
//
//   • The PREVIEW area uses a separate, narrower selector
//     (`buildPreviewRows`) that returns ONLY exportable artefacts.
//
//   • Chip labels read "Source CV NDOR CV verdict {N}/100" and
//     "Tailored CV NDOR CV verdict {N}/100" via the strip's
//     score-suffix so users see the metric they're looking at.
//     We do NOT show the overall fit_score on these chips —
//     overall fit lives on the Pack page's Fit panel.
function buildArtefactStrip(pack) {
  const snap = pack?.pack_snapshot || {};

  // ── U-14.A — CV SUB-SCORE chip source-of-truth rewrite ─────────
  // Source CV sub-score — fallback CHAIN (all SUB-SCORE sources;
  // NEVER falls back to overall fit_score / fit_score_at_finalize):
  //   1. `snap.source_baseline.cv_sub_score`
  //        — U-14.B field on packs created post-2026-02-15.
  //   2. `snap.source_baseline.sub_scores.cv_sub_score`
  //        — pre-existing nested-form backfill the backend already
  //        emits on legacy snapshots (verified live 2026-02-15:
  //        pack 280cd76c → sub_scores.cv_sub_score: 18, etc.).
  //   3. `null` → chip degrades to 'legacy' kind → renders "—"
  //        (operator-locked: never fall back to overall fit_score).
  const sourceIncluded = !!pack?.base_cv_id;
  const sourceSub = (() => {
    const sb = snap?.source_baseline;
    if (!sb || typeof sb !== 'object') return null;
    if (typeof sb.cv_sub_score === 'number') return sb.cv_sub_score;
    const nested = sb.sub_scores;
    if (nested && typeof nested === 'object' && typeof nested.cv_sub_score === 'number') {
      return nested.cv_sub_score;
    }
    return null;
  })();

  // Tailored CV sub-score — fallback CHAIN (all SUB-SCORE sources):
  //   1. `snap.cv_verdict.cv_sub_score`
  //        — modern object-form (post-RC 1.50).
  //   2. `typeof snap.cv_verdict === 'number'` → scalar form
  //        — legacy snapshots (pre-RC 1.50, verified live 2026-
  //        02-15: pack 1414fa1c → snap.cv_verdict = 96 as a scalar).
  //   3. `pack.cv_variant.cv_sub_score_at_finalize`
  //        — top-level cv_variants column; backend ships it on the
  //        list endpoint at L952 of application_packs.py.
  //   4. `null` → chip degrades to 'na' / 'legacy' per the truth
  //        table.
  const tailoredIncluded = !!pack?.cv_variant_id;
  const tailoredSub = (() => {
    const v = snap?.cv_verdict;
    if (v && typeof v === 'object' && typeof v.cv_sub_score === 'number') {
      return v.cv_sub_score;
    }
    if (typeof v === 'number') return v;
    if (pack?.cv_variant && typeof pack.cv_variant.cv_sub_score_at_finalize === 'number') {
      return pack.cv_variant.cv_sub_score_at_finalize;
    }
    return null;
  })();

  // Tailored chip display via the U-14 truth-table helper.
  const tailoredChip = computeChipDisplay({
    variantExists: tailoredIncluded,
    sourceSub,
    tailoredSub,
  });

  // Source CV chip display:
  //   • Variant in pack OR not — the chip is provenance and
  //     always shows the source sub-score WHEN AVAILABLE.
  //   • sourceSub null + sourceIncluded → 'legacy' kind →
  //     chip renders "—" (operator-locked: NEVER fall back to
  //     overall fit_score).
  let sourceChipKind;
  let sourceChipText;
  let sourceChipRaw;
  if (!sourceIncluded) {
    sourceChipKind = 'absent';
    sourceChipText = null;
    sourceChipRaw  = null;
  } else if (typeof sourceSub === 'number') {
    sourceChipKind = 'score';
    sourceChipText = String(sourceSub);
    sourceChipRaw  = sourceSub;
  } else {
    sourceChipKind = 'legacy';
    sourceChipText = '—';
    sourceChipRaw  = null;
  }

  // Cover Letter
  const letterIncluded = !!pack?.cover_letter_id;

  // Interview Prep — RC 1.51 contract gate.
  const ipTop = snap.interview_prep;
  const ipLegacy = snap?.fit?.reasoning_json?.interview_prep;
  const ipIncluded = !!(
    (ipTop && typeof ipTop === 'object' && Object.keys(ipTop).length > 0)
    || (ipLegacy && typeof ipLegacy === 'object' && Object.keys(ipLegacy).length > 0)
  );

  return [
    {
      kind: 'base_cv',
      // U-14 label clarity — make the metric explicit so the
      // chip can never be confused with the pack's overall fit.
      label: 'Source CV',
      metricLabel: 'CV sub-score',
      included: sourceIncluded,
      score: sourceChipRaw,
      scoreText: sourceChipText,
      scoreKind: sourceChipKind,   // 'score' | 'legacy' | 'absent'
      id: pack?.base_cv_id || null, Icon: FileText,
    },
    {
      kind: 'variant',
      label: 'Tailored CV',
      metricLabel: 'CV sub-score',
      included: tailoredIncluded,
      // Numeric `score` drives the tone class; only set when the
      // helper resolved a real improvement-above-threshold OR a
      // legacy-rendered tailored sub-score.
      score: (tailoredChip.kind === 'score' || tailoredChip.kind === 'legacy')
        ? tailoredChip.raw : null,
      scoreText: tailoredChip.kind === 'na' ? 'N/A' : tailoredChip.text,
      scoreKind: tailoredChip.kind, // 'score' | 'not_needed' | 'na' | 'legacy'
      id: pack?.cv_variant_id || null, Icon: FileText,
    },
    {
      kind: 'letter', label: 'Cover Letter', metricLabel: null,
      included: letterIncluded, score: null, scoreText: null, scoreKind: 'absent',
      id: pack?.cover_letter_id || null, Icon: Mail,
    },
    {
      kind: 'interview_prep', label: 'Interview Prep', metricLabel: null,
      included: ipIncluded, score: null, scoreText: null, scoreKind: 'absent',
      id: null, Icon: Sparkles,
    },
  ];
}

// Preview area artefact selector — operator-locked semantics
// (2026-05-12 PM): EXPORTABLE artefacts only. Source CV is NEVER
// rendered as a preview tile because it is not part of the
// application pack export — it is provenance only.
function buildPreviewRows(stripRows) {
  return stripRows.filter((r) => r.kind !== 'base_cv');
}

// ── Score tone helper. ─────────────────────────────────────────
function scoreTone(score) {
  if (typeof score !== 'number') return 'text-ndor-ink-muted';
  if (score >= 70) return 'text-ndor-success';
  if (score >= 40) return 'text-ndor-warning';
  return 'text-ndor-danger';
}

// ── Date helper ─────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return ''; }
}

// ── ReadinessStripChip — collapsed-state strip atom. ─────────────
//
// Score rendering rules (operator-locked, 2026-05-12 PM polish):
//   • `row.scoreText` is the AUTHORITATIVE display string.
//   • If `row.scoreText` is a string like "97" the chip renders
//     the number tinted by `scoreTone(row.score)`.
//   • If `row.scoreText` is "N/A" (or "—") the chip renders the
//     literal text in muted tone — used for the Tailored CV row
//     when the variant did NOT improve on the Source CV baseline.
//   • If `row.scoreText` is null no score chunk is rendered at all
//     (used for Cover Letter / Interview Prep / strip rows with no
//     score concept).
function ReadinessStripChip({ row }) {
  return (
    <span
      data-testid={`application-pack-strip-chip-${row.kind}`}
      data-included={row.included ? 'true' : 'false'}
      data-score-text={row.scoreText || ''}
      data-score-kind={row.scoreKind || ''}
      data-metric-label={row.metricLabel || ''}
      // U-14.A — `aria-label` makes the metric explicit for AT
      // users: "Source CV CV sub-score 78" / "Tailored CV CV
      // sub-score Not needed". The chip text itself stays compact.
      aria-label={[
        row.label,
        row.metricLabel,
        row.scoreText || (row.included ? '' : 'not included'),
      ].filter(Boolean).join(' ')}
      className={[
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full',
        'text-[11px] font-medium border',
        row.included
          ? 'bg-ndor-success-light/60 text-ndor-success border-ndor-success/30'
          // RC 1.54 contrast bump — disabled chips were too faint
          // (`text-ndor-ink-muted/70` against the dark surface). Up
          // to full `text-ndor-ink-secondary` so the chip remains
          // legible while still reading as "not included".
          : 'bg-transparent text-ndor-ink-secondary border-ndor-edge line-through decoration-ndor-ink-muted/60',
      ].join(' ')}
    >
      {row.included
        ? <CheckCircle2 className="h-3 w-3" aria-hidden />
        : <MinusCircle  className="h-3 w-3 opacity-70" aria-hidden />}
      <span className="no-underline">{row.label}</span>
      {row.scoreText && (
        <span
          className={[
            'tabular-nums font-semibold no-underline',
            // U-14 — only numeric 'score' / 'legacy' kinds use the
            // tone class; 'not_needed' / 'na' render muted so they
            // never visually imply a numeric improvement.
            (typeof row.score === 'number' && row.scoreKind === 'score')
              ? scoreTone(row.score)
              : 'text-ndor-ink-muted',
          ].join(' ')}
          data-testid={`application-pack-strip-chip-${row.kind}-score`}
        >
          {row.scoreText}
        </span>
      )}
    </span>
  );
}

// ── PreviewTile — expanded-panel tile, lazy-loads on click. ──────
//
// Subtitle rendering (operator-locked 2026-05-12 PM polish slice;
// U-14 sub-score clarity update 2026-02-15):
//   • Included + numeric 'score' kind    → "Included · CV sub-score 96"
//   • Included + 'not_needed' kind       → "Included · No material improvement"
//   • Included + 'legacy' kind           → "Included · CV sub-score 96 (legacy)"
//   • Included + 'na' kind               → "Included · CV sub-score N/A"
//   • Included, no score concept         → "Included"
//   • Not included                       → "Not included"
function PreviewTile({ row, packSnap, onPreview }) {
  let subtitle;
  if (!row.included) {
    subtitle = 'Not included';
  } else if (row.scoreKind === 'score' && typeof row.score === 'number') {
    subtitle = `Included · ${row.metricLabel || 'score'} ${row.score}`;
  } else if (row.scoreKind === 'not_needed') {
    subtitle = 'Included · No material improvement';
  } else if (row.scoreKind === 'legacy' && row.scoreText) {
    subtitle = `Included · ${row.metricLabel || 'score'} ${row.scoreText} (legacy)`;
  } else if (row.scoreKind === 'na' || row.scoreText === 'N/A') {
    subtitle = `Included · ${row.metricLabel || 'score'} N/A`;
  } else if (row.scoreText) {
    subtitle = `Included · ${row.metricLabel || 'score'} ${row.scoreText}`;
  } else {
    subtitle = 'Included';
  }
  return (
    <button
      type="button"
      data-testid={`application-pack-preview-${row.kind === 'base_cv' ? 'source-cv' : row.kind === 'variant' ? 'tailored-cv' : row.kind === 'letter' ? 'letter' : 'ip'}-${packSnap.id}`}
      data-included={row.included ? 'true' : 'false'}
      onClick={() => row.included && onPreview(row)}
      disabled={!row.included}
      title={row.included ? `Preview ${row.label}` : `${row.label} is not included on this pack`}
      className={[
        'group/tile flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition w-full',
        'bg-ndor-surface/70',
        row.included
          ? 'border-ndor-edge hover:border-ndor-ink-muted hover:bg-ndor-surface cursor-pointer'
          : 'border-ndor-edge/50 opacity-60 cursor-not-allowed',
      ].join(' ')}
    >
      <row.Icon
        className={[
          'h-4 w-4 shrink-0',
          row.included ? 'text-ndor-ink' : 'text-ndor-ink-muted',
        ].join(' ')}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-medium text-ndor-ink truncate">{row.label}</p>
        <p className="text-[10px] text-ndor-ink-muted truncate">{subtitle}</p>
      </div>
      {row.included
        ? (
          <span className="inline-flex items-center gap-1 text-[10px] text-ndor-ink-muted">
            <Eye className="h-3 w-3" aria-hidden />
            <ChevronRight className="h-3 w-3 opacity-60 transition-transform group-hover/tile:translate-x-0.5" aria-hidden />
          </span>
        )
        : <Lock className="h-3 w-3 text-ndor-ink-muted/60" aria-hidden />}
    </button>
  );
}

// ── Preview Drawer (Sheet) + lazy artefact-body fetch. ───────────
function PreviewDrawer({ open, onOpenChange, row, ipPayload, fallbackTailoredName, fallbackLetterName, sourceCvName }) {
  const [body, setBody] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !row) { setBody(null); setError(''); return; }

    // Interview Prep is already on the snapshot — no fetch.
    if (row.kind === 'interview_prep') {
      const ip = ipPayload || {};
      const lines = [];
      if (ip.headline) lines.push(ip.headline);
      if (Array.isArray(ip.likely_questions) && ip.likely_questions.length) {
        lines.push('');
        lines.push('Likely questions:');
        for (const q of ip.likely_questions) {
          const cat = q.category ? ` (${q.category})` : '';
          lines.push(`• ${q.question}${cat}`);
        }
      }
      setBody({ kind: 'text', text: lines.join('\n') || null });
      return;
    }

    // CV variant / cover letter / source cv — lazy-load via existing
    // read endpoints (same routes PackPreviewPanel uses).
    if (!row.id) { setBody(null); return; }
    const path = row.kind === 'variant'
      ? `/api/cv-variants/${row.id}`
      : row.kind === 'letter'
        ? `/api/cover-letters/${row.id}`
        : row.kind === 'base_cv'
          ? `/api/base-cvs/${row.id}`
          : null;
    if (!path) { setBody(null); return; }

    let alive = true;
    setLoading(true); setError('');
    apiFetch(path)
      .then((d) => { if (alive) setBody({ kind: row.kind, data: d }); })
      .catch((e) => { if (alive) setError(e?.message || 'Failed to load preview.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, row, ipPayload]);

  const title = row
    ? (row.kind === 'variant' ? `Tailored CV — ${fallbackTailoredName || 'preview'}`
      : row.kind === 'letter' ? `Cover Letter — ${fallbackLetterName || 'preview'}`
        : row.kind === 'base_cv' ? `Source CV — ${sourceCvName || 'preview'}`
          : 'Interview Prep preview')
    : 'Preview';

  const cvMode = row?.kind === 'base_cv' ? 'source'
    : row?.kind === 'variant' ? 'variant'
      : row?.kind === 'letter' ? 'letter' : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto"
        data-testid="application-card-preview-drawer"
        data-preview-kind={row?.kind || ''}
      >
        <SheetHeader>
          <SheetTitle
            className="flex items-center gap-2"
            data-testid="application-card-preview-title"
          >
            {title}
            <Badge
              variant="outline"
              className="text-[10px] tracking-[0.16em] uppercase border-ndor-edge text-ndor-ink-muted"
            >
              <Lock className="h-2.5 w-2.5 mr-1" />
              Read-only
            </Badge>
          </SheetTitle>
          <SheetDescription>
            Editing happens in the Workspace. Previews here are read-only.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-ndor-ink-muted" />
            </div>
          )}
          {!loading && error && (
            <p
              className="text-sm text-ndor-danger"
              data-testid="application-card-preview-error"
            >
              {error}
            </p>
          )}
          {!loading && !error && body && body.kind === 'text' && body.text && (
            <pre
              className="whitespace-pre-wrap text-sm leading-relaxed text-ndor-ink-secondary font-sans"
              data-testid="application-card-preview-body"
            >
              {body.text}
            </pre>
          )}
          {!loading && !error && body && body.kind === 'text' && !body.text && (
            <p
              className="text-ndor-ink-muted italic text-sm"
              data-testid="application-card-preview-empty"
            >
              No content available for this preview.
            </p>
          )}
          {!loading && !error && body && cvMode && (
            <div data-testid="application-card-preview-body">
              <CvRenderer mode={cvMode} data={body.data} />
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Main card ────────────────────────────────────────────────────
export default function ApplicationCard({
  pack,
  job,
  variant,
  letter,
  selected,
  onToggleSelected,
  expanded,
  onToggleExpanded,
  isPulsing,
  onPackUpdated,
}) {
  const navigate = useNavigate();
  // RC 1.58 Phase 4 (operator-locked, 2026-02-14) — one-time
  // acknowledgement modal between the "Open Workspace" CTA and
  // the actual `/applications/workspace/:jobId` route push. UI-only.
  const {
    guardedNavigate: guardedWorkspaceNavigate,
    dialogProps: workspaceAckDialogProps,
  } = useWorkspaceAcknowledgement();

  const jobTitle = useMemo(() => {
    if (job && !job.__removed) return job.title;
    if (job && job.__removed) return 'Job removed by source';
    if (!pack.job_id) return 'Pack without linked job';
    return 'Job removed by source';
  }, [job, pack.job_id]);
  const company  = (job && !job.__removed) ? job.company  : null;
  const location = (job && !job.__removed) ? job.location : null;

  const status = pack.pack_status || 'draft';
  const statusInfo = statusMeta(status);

  // Governance — canonical_fit FIRST, snapshot fit fallback.
  const finalDecision =
    pack?.canonical_fit?.final_decision
    || pack?.pack_snapshot?.fit?.final_decision
    || null;
  const rec = recommendationPill(finalDecision);

  const strip = useMemo(() => buildArtefactStrip(pack), [pack]);

  // Preview area = exportable artefacts only (Tailored CV / Cover
  // Letter / Interview Prep). Source CV is provenance and is NOT
  // shown as a preview tile.
  const previewRows = useMemo(() => buildPreviewRows(strip), [strip]);

  // ── Editing state ──
  const [statusDraft, setStatusDraft] = useState(status);
  const [notesDraft, setNotesDraft]   = useState(pack.notes || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!expanded) {
      setStatusDraft(status);
      setNotesDraft(pack.notes || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pack.id, pack.notes, pack.pack_status, expanded]);

  const dirty = statusDraft !== status || (notesDraft || '') !== (pack.notes || '');

  const handleCancel = () => {
    setStatusDraft(status);
    setNotesDraft(pack.notes || '');
  };

  const handleSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const body = {};
      if (statusDraft !== status)              body.pack_status = statusDraft;
      if ((notesDraft || '') !== (pack.notes || '')) body.notes = notesDraft;
      const updated = await apiFetch(`/api/application-packs/${pack.id}`, {
        method: 'PATCH',
        body,
      });
      toast.success('Changes saved.');
      cacheInvalidate(['applications', 'application-packs:all']);
      onPackUpdated?.(updated);
    } catch (e) {
      toast.error(e?.data?.detail || e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // ── Quick actions ──
  const [bundling, setBundling] = useState(false);
  const handleDownloadPack = async () => {
    if (bundling) return;
    setBundling(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated.');
      const res = await fetch(
        `${BACKEND_URL}/api/application-packs/${pack.id}/export-bundle`,
        { headers: { Authorization: `Bearer ${session.access_token}` } },
      );
      if (!res.ok) {
        let msg = `Download failed (${res.status})`;
        try { const j = await res.json(); msg = j.detail || msg; } catch { /* binary */ }
        throw new Error(msg);
      }
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : 'ApplicationPack.zip';
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Small delay before revoke so the browser commits the download
      // (Chrome occasionally cancels if the object URL is freed too soon).
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      toast.error(err?.message || 'Download failed.');
    } finally {
      setBundling(false);
    }
  };

  const handleOpenWorkspace = () => {
    if (!pack.job_id) return;
    // ── U-12.FE.A — Pack → Workspace URL handles (2026-02-15) ────
    //
    // OPERATOR-LOCKED. The pack carries explicit document IDs that
    // ARE the user's intended composition for this job. Pass them
    // through as URL query params so the workspace's existing URL-
    // bound rehydrate effects bind exactly — NO by-job lookup, NO
    // context confusion, NO race between selectedCv-URL-sync and
    // discovery-hook stamping.
    //
    // URL handles are explicit intent — the URL-handle bind path is
    // intentionally source-tolerant (U-10.IP.B). The by-job discovery
    // path remains source-context strict (U-12.FE.C).
    const params = new URLSearchParams();
    if (pack.cv_variant_id) {
      params.set('last_variant_id', pack.cv_variant_id);
      params.set('source_cv_id',    pack.cv_variant_id);
      params.set('source_cv_type',  'cv_variant');
    } else if (pack.base_cv_id) {
      params.set('source_cv_id',    pack.base_cv_id);
      params.set('source_cv_type',  'base_cv');
    }
    if (pack.cover_letter_id) {
      params.set('cover_letter_id',   pack.cover_letter_id);
    }
    if (pack.interview_prep_id) {
      params.set('interview_prep_id', pack.interview_prep_id);
    }
    // Stamp a pack-origin marker so the discovery hook can recognise
    // this entry path and respect the pack's exact composition (e.g.,
    // an empty IP when pack.interview_prep_id is null).
    params.set('from_pack', pack.id || '1');
    const qs = params.toString();
    const path = `/applications/workspace/${pack.job_id}${qs ? `?${qs}` : ''}`;
    guardedWorkspaceNavigate(path);
  };

  // ── Gmail send (inline drawer, no detail-page jump). ──
  // RC 1.53 (2026-05-12 PM, operator-locked) — Applications page is
  // now the operational tracker. Send via Gmail opens the existing
  // `SendViaGmailDialog` inline. If Gmail is not yet connected we
  // pop the same OAuth window Pack Detail uses; the existing
  // `message` listener on Pack Detail still finalises the token —
  // and on the next page-load the dialog will see `connected=true`.
  // The legacy `/applications/:id#gmail` route is preserved (not
  // deleted) so any external bookmark continues to work.
  const [gmailDialogOpen, setGmailDialogOpen] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [gmailEmail, setGmailEmail] = useState(null);
  const [gmailChecking, setGmailChecking] = useState(false);

  const loadGmailStatus = useCallback(async () => {
    setGmailChecking(true);
    try {
      const s = await apiFetch('/api/gmail/status');
      setGmailConnected(!!s.connected);
      setGmailEmail(s.email || null);
      return !!s.connected;
    } catch {
      setGmailConnected(false);
      setGmailEmail(null);
      return false;
    } finally {
      setGmailChecking(false);
    }
  }, []);

  const handleSendViaGmail = async () => {
    if (gmailChecking) return;
    const connected = await loadGmailStatus();
    if (connected) {
      setGmailDialogOpen(true);
      return;
    }
    // Not connected — start the OAuth popup. The Applications page
    // owns the `message` listener that finalises the token (single
    // listener for all cards) and dispatches a `ndorflow-gmail-connected`
    // window event so any card on the page picks up the new status.
    try {
      const { auth_url } = await apiFetch('/api/gmail/oauth/start');
      window.open(auth_url, 'ndorflow-gmail-oauth', 'width=540,height=680');
      toast.info('Connect Gmail in the popup, then click Send via Gmail again.');
    } catch (err) {
      toast.error(err?.message || 'Could not start Gmail OAuth.');
    }
  };

  // Page-level Gmail-connected broadcast → refresh this card's local
  // Gmail status without polling. (See ApplicationsPage for the single
  // `message`-event listener that triggers this.)
  useEffect(() => {
    function onConnected(ev) {
      setGmailConnected(true);
      setGmailEmail(ev?.detail?.email || null);
    }
    window.addEventListener('ndorflow-gmail-connected', onConnected);
    return () => window.removeEventListener('ndorflow-gmail-connected', onConnected);
  }, []);

  // ── Preview drawer ──
  const [previewRow, setPreviewRow] = useState(null);

  return (
    <Card
      data-testid={`application-pack-row-${pack.id}`}
      data-pulse={isPulsing ? 'true' : undefined}
      data-expanded={expanded ? 'true' : undefined}
      data-pack-status={status}
      className={[
        'group overflow-hidden transition-all duration-200',
        'border border-ndor-edge bg-ndor-surface/95 backdrop-blur-sm',
        'rounded-2xl shadow-sm',
        selected ? 'border-ndor-ink-muted bg-ndor-surface-alt/40' : 'hover:border-ndor-ink-muted/70',
        isPulsing ? 'ring-2 ring-ndor-gold/60 animate-pulse' : '',
        expanded ? 'shadow-md' : '',
      ].join(' ')}
    >
      {/* ───────────── COLLAPSED HEADER ───────────── */}
      <div className="flex items-stretch gap-3 px-4 py-3.5 sm:px-5">
        <div
          className="pt-1 shrink-0"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="presentation"
        >
          <Checkbox
            checked={selected}
            onCheckedChange={(v) => onToggleSelected?.(pack.id, !!v)}
            aria-label={`Select pack ${pack.id}`}
            data-testid={`application-pack-select-${pack.id}`}
          />
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          {/* Title + status + governance */}
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 space-y-0.5">
              <div className="flex items-center gap-2 flex-wrap min-w-0">
                <Briefcase className="h-4 w-4 text-ndor-ink-secondary shrink-0" aria-hidden />
                <button
                  type="button"
                  onClick={() => onToggleExpanded?.(pack.id)}
                  className="text-sm font-semibold text-ndor-ink hover:underline truncate text-left"
                  data-testid={`application-pack-title-${pack.id}`}
                  aria-expanded={expanded}
                >
                  {jobTitle}
                </button>
              </div>
              {/* RC 1.54 contrast — moved metadata from
                  `text-ndor-ink-muted` (too faint at 11px on the
                  card surface) to `text-ndor-ink-secondary`. */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-ndor-ink-secondary">
                {company && (
                  <span data-testid={`application-pack-company-${pack.id}`}>{company}</span>
                )}
                {location && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3 w-3" aria-hidden />
                    {location}
                  </span>
                )}
                <span>Created {fmtDate(pack.created_at)}</span>
                {pack.updated_at && pack.updated_at !== pack.created_at && (
                  <span>· Updated {fmtDate(pack.updated_at)}</span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1.5 flex-wrap justify-end">
              <Badge
                variant="outline"
                className={`text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 ${statusInfo.className}`}
                data-testid={`application-pack-status-${pack.id}`}
              >
                {statusInfo.label}
              </Badge>
              {rec && (
                <Badge
                  variant="outline"
                  className={`text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 inline-flex items-center gap-1 ${rec.className}`}
                  data-testid={`application-pack-recommendation-${pack.id}`}
                  data-tone={rec.tone}
                >
                  <rec.Icon className="h-3 w-3" aria-hidden />
                  {rec.label}
                </Badge>
              )}
            </div>
          </div>

          {/* RC 1.54 — strip + actions grouped into a single tight
              row on wide screens, stacks vertically on narrow. This
              brings Download / Send via Gmail / Open Workspace
              visually closer to the artefact chips they operate on.
              Below 640 px (mobile) the actions wrap underneath the
              strip — never below the Details chevron. */}
          <div
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
            data-testid={`application-pack-row-actions-${pack.id}`}
          >
            <div
              className="flex flex-wrap items-center gap-1.5"
              data-testid={`application-pack-strip-${pack.id}`}
            >
              {strip.map((row) => (
                <ReadinessStripChip key={row.kind} row={row} />
              ))}
            </div>

            <div className="flex items-center gap-1.5 flex-wrap sm:justify-end">
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2.5 text-[11px]"
                onClick={handleDownloadPack}
                disabled={bundling}
                data-testid={`application-pack-download-${pack.id}`}
                title="Download Application Pack"
              >
                {bundling
                  ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  : <Download  className="h-3 w-3 mr-1" />}
                Download
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2.5 text-[11px]"
                onClick={handleSendViaGmail}
                disabled={gmailChecking}
                data-testid={`application-pack-send-gmail-${pack.id}`}
                title={gmailConnected
                  ? `Send via Gmail (${gmailEmail || 'connected'})`
                  : 'Send via Gmail'}
              >
                {gmailChecking
                  ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  : <Send    className="h-3 w-3 mr-1" />}
                Send via Gmail
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2.5 text-[11px]"
                onClick={handleOpenWorkspace}
                disabled={!pack.job_id}
                data-testid={`application-pack-open-workspace-${pack.id}`}
                title="Open Workspace"
              >
                <PanelsTopLeft className="h-3 w-3 mr-1" />
                Open Workspace
              </Button>
              {/* RC 1.54 — Details chevron promoted into the same
                  action cluster so it visually groups with the
                  pack actions instead of floating on its own row. */}
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-[11px] text-ndor-ink-secondary"
                onClick={() => onToggleExpanded?.(pack.id)}
                data-testid={`application-pack-expand-${pack.id}`}
                aria-expanded={expanded}
              >
                {expanded
                  ? <><ChevronUp   className="h-3 w-3 mr-1" />Collapse</>
                  : <><ChevronDown className="h-3 w-3 mr-1" />Details</>}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ───────────── EXPANDED BODY ───────────── */}
      {expanded && (
        <div
          className="border-t border-ndor-edge bg-ndor-surface-alt/30 px-5 py-4"
          data-testid={`application-pack-expanded-${pack.id}`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
            {/* LEFT COLUMN — status + notes + Save/Cancel + deep link */}
            <div className="lg:col-span-2 space-y-3">
              <div className="space-y-1">
                <label
                  htmlFor={`status-${pack.id}`}
                  className="text-[10px] uppercase tracking-[0.12em] text-ndor-ink-muted font-medium"
                >
                  Status
                </label>
                <Select value={statusDraft} onValueChange={setStatusDraft}>
                  <SelectTrigger
                    id={`status-${pack.id}`}
                    className="h-9 text-xs"
                    data-testid={`application-pack-status-select-${pack.id}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((opt) => (
                      <SelectItem
                        key={opt.value}
                        value={opt.value}
                        data-testid={`application-pack-status-option-${opt.value}`}
                      >
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <label
                  htmlFor={`notes-${pack.id}`}
                  className="text-[10px] uppercase tracking-[0.12em] text-ndor-ink-muted font-medium"
                >
                  Notes
                </label>
                <Textarea
                  id={`notes-${pack.id}`}
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  placeholder="Private notes about this application…"
                  rows={6}
                  className="text-xs resize-y min-h-[7rem]"
                  data-testid={`application-pack-notes-${pack.id}`}
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 px-3 text-[11px]"
                  onClick={handleCancel}
                  disabled={!dirty || saving}
                  data-testid={`application-pack-cancel-${pack.id}`}
                >
                  <X className="h-3 w-3 mr-1" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="h-8 px-3 text-[11px]"
                  onClick={handleSave}
                  disabled={!dirty || saving}
                  data-testid={`application-pack-save-${pack.id}`}
                >
                  {saving
                    ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    : <Save     className="h-3 w-3 mr-1" />}
                  Save
                </Button>
              </div>
            </div>

            {/* RIGHT COLUMN — preview tiles */}
            <div className="lg:col-span-3 space-y-2">
              <p className="text-[10px] uppercase tracking-[0.12em] text-ndor-ink-muted font-medium">
                Preview included artefacts
              </p>
              <div
                className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                data-testid={`application-pack-preview-tiles-${pack.id}`}
              >
                {previewRows.map((row) => (
                  <PreviewTile
                    key={row.kind}
                    row={row}
                    packSnap={pack}
                    onPreview={setPreviewRow}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <PreviewDrawer
        open={!!previewRow}
        onOpenChange={(v) => { if (!v) setPreviewRow(null); }}
        row={previewRow}
        ipPayload={
          pack?.pack_snapshot?.interview_prep
          || pack?.pack_snapshot?.fit?.reasoning_json?.interview_prep
          || null
        }
        fallbackTailoredName={variant?.variant_name || variant?.role_family || ''}
        fallbackLetterName={letter?.title || letter?.target_role || ''}
        sourceCvName={(pack?.base_cv && (pack.base_cv.file_name || pack.base_cv.name)) || ''}
      />

      {/* Inline Gmail composer — RC 1.53 operator-locked. The Applications
          page is the operational tracker; this drawer keeps the user on
          the page instead of bouncing to Pack Detail. The legacy
          `/applications/:id#gmail` route is preserved (not removed) so
          existing deep links keep working. */}
      <SendViaGmailDialog
        open={gmailDialogOpen}
        onOpenChange={setGmailDialogOpen}
        pack={{ ...pack, job }}
        onDraftCreated={() => {
          toast.success('Draft staged in Gmail.');
          cacheInvalidate(['applications', 'application-packs:all']);
        }}
      />
      {/* RC 1.58 Phase 4 — one-time acknowledgement modal between
          this card's "Open Workspace" CTA and the workspace route.
          UI-only — no billing / orchestration / session-guard. */}
      <WorkspaceAcknowledgementDialog {...workspaceAckDialogProps} />
    </Card>
  );
}
